import { useEffect, useState } from "react";
import { type NumberFormatValues } from "react-number-format";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon } from "vega-icons";

import {
  getGetDealTrackingForGpQueryKey,
  useUpdateFundContributionAmountForDeal,
} from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { type ApiDealTrackingResponse } from "@vega-api/generated/types/apiDealTrackingResponse";
import { type Currency } from "@vega-api/generated/types/currency";
import { type DealTrackingMetric } from "@vega-api/generated/types/dealTrackingMetric";
import { type Money } from "@vega-api/generated/types/money";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { MoneyInput } from "@vega-core/components/Form/Controls/Input/MoneyInput/MoneyInput";
import { MetricCard } from "@vega-core/components/MetricCard/MetricCard";
import { ProgressBar } from "@vega-core/components/ProgressBar/ProgressBar";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import { formatMoneyShort } from "@vega-core/utils/formatMoney";
import { slugify } from "@vega-core/utils/slugify";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { useCanEditDealOpportunity } from "../../hooks/useCanEditDealOpportunity";

type AllocationSummaryPanelProps = {
  metrics: DealTrackingMetric[];
  fundContributionAmount?: Money;
  dealId: string;
  dealCurrency: Currency;
  isLoading?: boolean;
};

const isCurrencyMetric = (
  metric: DealTrackingMetric,
): metric is Extract<
  DealTrackingMetric,
  { currencyMetricDiscriminator: "TRUE" }
> => "currencyMetricDiscriminator" in metric;

const isProgressBarMetric = (
  metric: DealTrackingMetric,
): metric is Extract<
  DealTrackingMetric,
  { progressBarMetricDiscriminator: "TRUE" }
> => "progressBarMetricDiscriminator" in metric;

const LABEL_LP_CO_INVEST_TARGET = "LP Co-Invest Target";

const LOADING_CARD_COUNT = 4;
const LOADING_BAR_COUNT = 2;

export const AllocationSummaryPanel = ({
  metrics,
  fundContributionAmount,
  dealId,
  dealCurrency,
  isLoading = false,
}: AllocationSummaryPanelProps) => {
  const committedFundContribution = fundContributionAmount?.amount ?? 0;
  const [draftFundContribution, setDraftFundContribution] = useState<number>(
    committedFundContribution,
  );

  useEffect(() => {
    setDraftFundContribution(committedFundContribution);
  }, [committedFundContribution]);

  const { canEditDealOpportunity } = useCanEditDealOpportunity(dealId);

  const { data: totalDealSize, isLoading: isDealViewLoading } =
    useGetDealGeneralPartnerViewV3(dealId, {
      query: {
        enabled: Boolean(dealId),
        select: (response) => response.data?.deal.totalDealSize,
      },
    });

  const queryClient = useQueryClient();
  const showErrorToast = useCoInvestErrorToast();

  const dealTrackingQueryKey = getGetDealTrackingForGpQueryKey(dealId);

  const { mutate: updateFundContribution } =
    useUpdateFundContributionAmountForDeal({
      mutation: {
        onMutate: async ({ data }) => {
          await queryClient.cancelQueries({ queryKey: dealTrackingQueryKey });
          const previousData = queryClient.getQueryData(dealTrackingQueryKey);
          queryClient.setQueryData<ApiDealTrackingResponse>(
            dealTrackingQueryKey,
            (old) => {
              if (!old?.data) return old;

              return {
                ...old,
                data: {
                  ...old.data,
                  fundContributionAmount: data.fundContributionAmount,
                },
              };
            },
          );

          return { previousData };
        },
        onError: (error, _, context) => {
          queryClient.setQueryData(dealTrackingQueryKey, context?.previousData);
          showErrorToast(error, {
            defaultMessage: m.coInvestments_gpFundContribution_update_error(),
          });
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: dealTrackingQueryKey });
        },
      },
    });

  const currencyMetrics = metrics.filter(isCurrencyMetric);
  const progressBarMetrics = metrics.filter(isProgressBarMetric);

  const currency = fundContributionAmount?.currency ?? dealCurrency;

  const totalDealSizeAmount = totalDealSize?.amount ?? 0;
  const lpCoInvestTarget = totalDealSizeAmount - committedFundContribution;
  const isTotalDealSizeDefined = totalDealSizeAmount > 0;
  const isPanelLoading = isLoading || isDealViewLoading;

  const displayCurrencyMetrics = currencyMetrics.map((metric) =>
    metric.label === LABEL_LP_CO_INVEST_TARGET
      ? { ...metric, moneyValue: { amount: lpCoInvestTarget, currency } }
      : metric,
  );

  const handleValueChange = (values: NumberFormatValues) => {
    setDraftFundContribution(values.floatValue ?? 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    updateFundContribution({
      dealId,
      data: {
        fundContributionAmount: { amount: draftFundContribution, currency },
      },
    });
  };

  const titleId = `book-build-title-${dealId}`;

  if (!isPanelLoading && !isTotalDealSizeDefined) {
    return (
      <section
        aria-labelledby={titleId}
        className="border border-border/primary rounded-companyBorderRadius p-6 bg-background/subtle"
        data-testid="allocation-summary-panel"
      >
        <span className="sr-only" id={titleId}>
          {m.coInvestments_bookBuild_title()}
        </span>
        <div
          className="flex items-center justify-between gap-4"
          data-testid="allocation-summary-empty-state"
        >
          <p className="body-base-regular text-text/secondary">
            {m.coInvestments_bookBuild_emptyState_message()}
          </p>
          <ThemedButton
            dataTestId="allocation-configure-deal"
            icon={<ArrowRightIcon className="size-4" />}
            iconPosition="right"
            route={CoInvestmentFullPaths.CoInvestmentDetails.replace(
              ":id",
              dealId,
            )}
            variant="secondary"
          >
            {m.coInvestments_bookBuild_configureDeal()}
          </ThemedButton>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-busy={isPanelLoading ? true : undefined}
      aria-labelledby={titleId}
      className="border border-border/primary rounded-companyBorderRadius p-6 space-y-4 bg-background/subtle"
      data-testid="allocation-summary-panel"
    >
      <div className="flex justify-between items-center">
        {isPanelLoading ? (
          <>
            <span className="sr-only" id={titleId}>
              {m.coInvestments_bookBuild_title()}
            </span>
            <Skeleton aria-hidden="true" className="h-6 w-32" />
            <Skeleton aria-hidden="true" className="h-10 w-52" />
          </>
        ) : (
          <>
            <h2 className="heading-5" id={titleId}>
              {m.coInvestments_bookBuild_title()}
            </h2>
            <div
              data-testid="fund-contribution-input-wrapper"
              onKeyDown={handleKeyDown}
            >
              <MoneyInput
                allowAbbreviations
                currency={dealCurrency}
                disabled={!canEditDealOpportunity}
                label={m.coInvestments_gpFundContribution()}
                labelClassName="body-large-semibold text-text/primary"
                layout="horizontal"
                name="gp-fund-contribution"
                onValueChange={handleValueChange}
                value={draftFundContribution}
              />
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {isPanelLoading
          ? Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
              <MetricCard
                dataTestId={`allocation-metric-card-loading-${index}`}
                isLoading
                key={index}
                title=""
                value=""
              />
            ))
          : displayCurrencyMetrics.map((metric) => (
              <MetricCard
                dataTestId={`allocation-metric-card-${slugify(metric.label)}`}
                key={metric.label}
                periodContent={
                  metric.description ? (
                    <p className="body-base-regular text-text/primary">
                      {metric.description}
                    </p>
                  ) : undefined
                }
                title={metric.label}
                value={formatMoneyShort(metric.moneyValue)}
              />
            ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isPanelLoading
          ? Array.from({ length: LOADING_BAR_COUNT }, (_, index) => (
              <Skeleton
                aria-hidden="true"
                className="h-20 rounded-companyBorderRadius"
                key={index}
              />
            ))
          : progressBarMetrics.map((metric) => {
              const { currentValue } = metric.progressBarData;
              const progress =
                lpCoInvestTarget > 0
                  ? Math.min((currentValue / lpCoInvestTarget) * 100, 100)
                  : 0;
              const labelSlug = slugify(metric.label);
              const labelId = `progress-label-${labelSlug}`;

              return (
                <div
                  aria-labelledby={labelId}
                  className="border border-border/primary rounded-companyBorderRadius p-4 space-y-2 bg-background/primary"
                  data-testid={`allocation-progress-bar-${labelSlug}`}
                  key={metric.label}
                  role="group"
                >
                  <div className="flex justify-between">
                    <p
                      className="body-large-semibold text-text/primary"
                      id={labelId}
                    >
                      {metric.label}
                    </p>
                    <p
                      aria-hidden="true"
                      className="body-large-semibold"
                      data-testid={`allocation-progress-percent-${labelSlug}`}
                    >
                      {Math.round(progress)}%
                    </p>
                  </div>
                  <ProgressBar
                    ariaLabel={metric.label}
                    bgColor="bg-background/secondary"
                    progress={progress}
                    size={16}
                  />
                </div>
              );
            })}
      </div>
    </section>
  );
};
